// Version history with one-click restore (restores into the draft — nothing goes live until you publish).
import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { History, RotateCcw, CheckCircle2, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function VersionHistory({ t, headers, onRestored }) {
  const [busy, setBusy] = useState(null);
  const items = (t.history || []).slice().reverse();
  const restore = async (v) => {
    if (!window.confirm(`Restore version ${v} into your draft? Your current unsaved edits will be replaced. Live customer portals are not affected until you publish.`)) return;
    setBusy(v);
    try { const { data } = await axios.post(`${API}/api/fact-find/templates/${t.id}/restore`, { version: v }, { headers }); onRestored(data); toast.success(`Version ${v} restored to draft — review, then publish`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Could not restore'); } finally { setBusy(null); }
  };
  return (
    <div className="space-y-3" data-testid="tb-history">
      <p className="text-[13px] text-slate-600 leading-relaxed">Every publish is saved as a version. Customers who already have a portal keep the version they were sent.</p>
      {!items.length && <p className="rounded-2xl border border-dashed border-slate-200 px-4 py-6 text-center text-[13px] text-slate-500">Not published yet.</p>}
      <ol className="space-y-2">
        {items.map((h) => {
          const live = h.version === t.published_version;
          return (
            <li key={h.version} className={`rounded-2xl border px-3.5 py-3 ${live ? 'border-emerald-200 bg-emerald-50/50' : 'border-slate-200 bg-white'}`} data-testid={`tb-version-${h.version}`}>
              <div className="flex items-center gap-2">
                <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#15133A] text-white text-[11px] font-black">v{h.version}</span>
                <div className="min-w-0 flex-1"><p className="text-[13px] font-bold text-slate-900 truncate">{h.note || 'Published'}</p><p className="text-[11px] text-slate-500">{new Date(h.at).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })} · {h.by}</p></div>
                {live ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-white" data-testid={`tb-version-live-${h.version}`}><CheckCircle2 className="w-3 h-3" /> Live</span>
                  : <button type="button" disabled={!!busy || !h.snapshot} onClick={() => restore(h.version)} className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-bold text-slate-700 hover:border-slate-900 disabled:opacity-40" data-testid={`tb-restore-${h.version}`}>{busy === h.version ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />} Restore</button>}
              </div>
            </li>
          );
        })}
      </ol>
      <p className="inline-flex items-center gap-1 text-[11px] text-slate-400"><History className="w-3 h-3" /> Restoring creates a new draft; publish it to make it live.</p>
    </div>
  );
}
